import { Alert, Badge, Button, Card, Col, Container, Form, ListGroup, ListGroupItem, Row, Stack } from "react-bootstrap";
import Layout from "../../shared/components/Layout";
import Header from "../../shared/components/Header";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAddons, fetchFeatureAddons } from "../store/plansSlice";
import { useEffect, useMemo, useState } from "react";
import { createOrder, recalcTotals, resetOrderEntity, setAddon, setBillingAddress, setFeatureAddon, setPlan, updateAddonQty } from "../store/orderEntitySlice";
import { UilEdit } from "@iconscout/react-unicons";
import Name from "../component/fields/Name";
import PhoneNumber from "../component/fields/PhoneNumber";
import Address1 from "../component/fields/Address1";
import Address2 from "../component/fields/Address2";
import City from "../component/fields/City";
import Pincode from "../component/fields/Pincode";
import State from "../component/fields/State";
import Gstin from "../component/fields/Gstin";

const ManageAddons = () => {
  const formatAmount = (val) => {
    const num = Number(val || 0);
    return num.toFixed(2);
  };
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { accessToken } = useSelector((state) => state.auth);
  const user = useSelector((state) => state?.auth?.user);
  const { addons, featureAddons, currentPlan } = useSelector((state) => state.plans);
  const { order_items, grand_total, tax_total, taxable_value, status, order_billing_addresses } = useSelector((state) => state.orderEntity);
  const { currentTenant } = useSelector((state) => state.sharedSlice);
  const [isEditAddress, setIsEditAddress] = useState(false);

  // Clear any persisted order state on entry
  useEffect(() => {
    dispatch(resetOrderEntity());
  }, []);

  // Redirect away if on trial; add-ons not allowed on trial
  useEffect(() => {
    if (currentPlan?.plan?.is_trial === 1) {
      alert('Add-ons are not available during trial.');
      navigate('/plan/billing');
    }
  }, [currentPlan?.plan?.is_trial]);

  // Pre-fill billing with tenant info
  useEffect(() => {
    const billArray = {
      name: user?.name ?? null,
      phone_number: user?.phone ?? null,
      address1: currentTenant?.business_address ?? null,
      address2: null,
      city: null,
      pincode: null,
      state: null,
      gstin: currentTenant?.gstin ?? null,
    };
    Object.entries(billArray).forEach(([field, value]) => {
      dispatch(setBillingAddress({ field, value }));
    });
  }, []);

  // Load catalog
  useEffect(() => {
    dispatch(fetchAddons({ access_token: accessToken, tenant_id: currentTenant?.id }));
    dispatch(fetchFeatureAddons({ access_token: accessToken, tenant_id: currentTenant?.id }));
  }, []);

  // Initialize order as addon-only purchase: set current plan with zero price
  useEffect(() => {
    if (currentPlan?.plan?.id != null) {
      dispatch(setPlan({ plan_id: currentPlan.plan.id, price: 0 }));
    }
  }, [currentPlan?.plan?.id]);

  // Set users addon pricing row (index 1)
  useEffect(() => {
    if (addons && addons[0]) {
      dispatch(setAddon({ addon_id: addons[0]?.id, price: addons[0]?.price }));
    }
  }, [addons]);

  // Purchased addons from current subscription (by ID only)
  const purchasedAddonIds = useMemo(() => {
    const items = currentPlan?.subscription?.items || [];
    // Collect only addon IDs; ignore plan/subscription-only lines
    return new Set(
      items
        .map((it) => it?.addon_id ?? it?.addon?.id)
        .filter((v) => v != null)
    );
  }, [currentPlan?.subscription?.items]);

  // Join subscription items with all known addons (users + feature addons)
  // so we can show a list of already purchased add-ons with price.
  const purchasedAddons = useMemo(() => {
    const items = currentPlan?.subscription?.subscription_items || [];
    if (!items.length) return [];

    const catalog = [
      ...(Array.isArray(addons) ? addons : []),
      ...(Array.isArray(featureAddons) ? featureAddons : []),
    ];
    const byId = new Map(catalog.map((a) => [a.id, a]));
    const seen = new Set();

    return items
      // Only rows that actually represent an add-on (not the base plan)
      .filter((it) => it?.addon_id != null || it?.addon)
      .map((it, index) => {
        const rawId = it?.addon_id ?? it?.addon?.id;
        const id = rawId != null ? rawId : `sub-${index}`;
        if (seen.has(id)) return null;
        seen.add(id);

        const cat = rawId != null ? byId.get(rawId) : null;
        const label =
          cat?.label ??
          cat?.name ??
          it?.addon?.label ??
          it?.addon?.name ??
          it?.label ??
          it?.name;
        const price =
          cat?.price ??
          it?.price ??
          it?.addon?.price ??
          0;

        return label ? { id, label, price } : null;
      })
      .filter(Boolean);
  }, [currentPlan?.subscription?.items, addons, featureAddons]);

  // Features included by the current plan (not add-on purchases)
  const normalizeName = (v) => (typeof v === 'string' ? v : v?.name || '').toLowerCase();
  const { planFeatureNames, planFeatureIds } = useMemo(() => {
    const list = Array.isArray(currentPlan?.features)
      ? currentPlan.features
      : Array.isArray(currentPlan?.plan?.features)
        ? currentPlan.plan.features
        : [];
    return {
      planFeatureNames: new Set(list.map((f) => normalizeName(f)).filter(Boolean)),
      planFeatureIds: new Set(list.map((f) => (typeof f === 'object' ? f?.id : null)).filter((v) => v != null)),
    };
  }, [currentPlan]);

  // Filter available feature addons:
  // - active only
  // - flavor matches current tenant (or generic/both)
  // - not already purchased
  // - not already included in the plan features
  const availableFeatureAddons = useMemo(() => {
    const tenantFlavor = String(currentTenant?.flavor || "").toLowerCase();
    return (featureAddons || [])
      .filter((fa) => fa && fa.status !== 0)
      .filter((fa) => {
        const addonFlavor = String(fa.flavor || "").toLowerCase();
        if (!addonFlavor) return true;
        if (addonFlavor === "both") return true;
        return addonFlavor === tenantFlavor;
      })
      .filter((fa) => !purchasedAddonIds.has(fa.id))
      .filter((fa) => !planFeatureNames.has(String(fa?.name || '').toLowerCase()))
      .filter((fa) => !planFeatureIds.has(fa?.id));
  }, [featureAddons, purchasedAddonIds, planFeatureNames, planFeatureIds, currentTenant?.flavor]);

  // Multiple users active either via existing features or selecting feature addon now
  const selectedFeatureIds = useMemo(() => new Set(order_items.slice(2).map((it) => it.addon_id)), [order_items]);
  const activeMultipleUsers = useMemo(() => {
    // Already part of plan features
    const alreadyActive = planFeatureNames.has('multiple_users');
    // Selected as an add-on in this order
    const multipleUsersAddonIds = (featureAddons || [])
      .filter((f) => normalizeName(f?.name || f?.label) === 'multiple_users')
      .map((f) => f.id);
    const selectHas = order_items.slice(2).some((it) => multipleUsersAddonIds.includes(it.addon_id));
    return alreadyActive || selectHas;
  }, [planFeatureNames, featureAddons, order_items]);

  // For plans where user limit is effectively exhausted and multiple_users
  // is not already part of the plan, show guidance text instead of the
  // "enable multiple users add-on" hint.
  const isUserLimitExhausted = useMemo(() => {
    const label = String(currentPlan?.plan?.label || "").toLowerCase();
    const excluded = ['free', 'free_ce', 'trial', 'trial_ce', 'promo', 'promo_ce'];
    if (excluded.includes(label)) return false;
    const maxUsers = parseInt(currentPlan?.subscription?.max_users);
    const hasMultipleUsersFeature = planFeatureNames.has('multiple_users');
    return (maxUsers === 1 || isNaN(maxUsers)) && !hasMultipleUsersFeature;
  }, [currentPlan?.plan?.label, currentPlan?.subscription?.max_users, planFeatureNames]);

  const [addlUsers, setAddlUsers] = useState(0);
  const [finalUsersCount, setFinalUsersCount] = useState(0);
  const userAddonPrice = addons[0]?.price || 0;

  const handleAddlUsersChange = (qty) => {
    const val = parseInt(qty || 0);
    setAddlUsers(isNaN(val) ? 0 : val);
  };

  useEffect(() => {
    let finalQty = 0;
    if (activeMultipleUsers) {
      const included = parseInt(currentPlan?.subscription?.max_users);
      const currentUsers = parseInt(currentPlan?.subscription?.current_users);
      if (!isNaN(addlUsers)) {
        finalQty = (currentUsers + (addlUsers || 0)) - included;
        finalQty = finalQty < 0 ? 0 : finalQty;
      }
    }
    setFinalUsersCount(finalQty);
    dispatch(updateAddonQty(finalQty));
  }, [addlUsers, activeMultipleUsers, currentPlan]);

  useEffect(() => {
    dispatch(recalcTotals());
  }, [order_items]);

  useEffect(() => {
    if (status?.success) {
      alert("Order created. Redirecting to billing.");
      navigate("/plan/billing");
      dispatch(resetOrderEntity());
    } else if (status?.error) {
      alert(status?.errorMessage || "Unable to place order");
    }
  }, [status]);

  const handleToggleFeature = (addon) => {
    // Block if already purchased or already part of the plan features
    const nameKey = String(addon?.name || '').toLowerCase();
    if (purchasedAddonIds.has(addon.id) || planFeatureNames.has(nameKey) || planFeatureIds.has(addon?.id)) return;
    // Toggle via setFeatureAddon using read of orderEntity
    const exists = order_items.findIndex((it, i) => i > 1 && it.addon_id === addon.id) >= 0;
    dispatch(setFeatureAddon({ addon, enabled: !exists }));
  };

  const handleCreateOrder = () => {
    // Build payload similar to CreateOrder, but plan line is zero-priced
    const payload = {
      user_id: currentTenant?.pivot?.user_id,
      tenant_id: currentTenant?.id,
      subscription_id: currentPlan?.subscription?.id,
      order_items: (order_items || []).map((it) => ({ ...it })),
    };
    // Clean empty billing address if any
    // Intentionally omit order_billing_addresses here to reuse saved billing profile on server
    dispatch(createOrder({ access_token: accessToken, data: payload, tenantId: currentTenant?.id }));
  };

  return (
    <Layout>
      <Header
        title={"Manage Add-ons"}
        ItemActions={
          <Stack>
            <Button variant="outline-secondary" onClick={() => navigate("/plan/billing")}>Back</Button>
          </Stack>
        }
      />
      <Container className="px-4 pt-2 pb-0" fluid>
        <Row>
          <Col>
            <Alert variant="light" className="py-2 px-4 mx-3 my-2">
              <div className="d-flex align-items-start">
                <div>
                  <div className="fw-semibold">Manage your subscription add-ons</div>
                  <div className="small text-muted">Add or remove features from your current plan. We only show add-ons you don’t already own.</div>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>
      </Container>
      <Container fluid className="px-4 pb-4">
        <Row className="mt-2">
          <Col md={8}>
            <Card>
              <Card.Header className="border h4">Current Plan</Card.Header>
              <Card.Body className="p-2 border">
                <ListGroup variant="flush">
                  <ListGroupItem>
                    <div className="d-flex justify-content-between">
                      <div>
                        <Badge bg="success" style={{ fontSize: "20px" }}>{currentPlan?.plan?.label}</Badge>
                        <span className="mx-3">Users: {currentPlan?.subscription?.current_users} / {currentPlan?.subscription?.max_users}</span>
                      </div>
                    </div>
                  </ListGroupItem>
                  {purchasedAddons.length > 0 && (
                    <ListGroupItem>
                      <div className="text-muted mb-1">Purchased Add-ons</div>
                      <div className="mt-1">
                        {purchasedAddons.map((pa) => (
                          <div key={pa.id} className="d-flex justify-content-between small mb-1">
                            <span>{pa.label}</span>
                            <span>₹&nbsp;{formatAmount(pa.price)}</span>
                          </div>
                        ))}
                      </div>
                    </ListGroupItem>
                  )}
                </ListGroup>
              </Card.Body>
            </Card>

            <Card className="mt-5 mb-5">
              <Card.Header className="border h4">Add-ons</Card.Header>
              <Card.Body className="p-2 border">
                <Card.Title className="text-success">Additional Users</Card.Title>
                {activeMultipleUsers ? (
                  <ListGroup variant="flush" className="mb-3">
                    <ListGroupItem className="justify-content-between" as={Stack} gap={4} direction="horizontal">
                      <div>
                        <span>Additional Users</span> (₹&nbsp;{formatAmount(userAddonPrice)} / User)
                      </div>
                      <span className="d-flex align-items-center">
                        <Form.Control
                          type="number"
                          className="mx-2 text-center"
                          style={{ width: "100px" }}
                          onChange={(e) => handleAddlUsersChange(e.target.value)}
                        />
                      </span>
                    </ListGroupItem>
                    <ListGroupItem className="justify-content-between" as={Stack} gap={4} direction="horizontal">
                      <div>
                        <span>Paid Users</span>
                      </div>
                      <span className="d-flex align-items-center">{finalUsersCount}</span>
                    </ListGroupItem>
                  </ListGroup>
                ) : (
                  <ListGroup className="mb-3" variant="flush">
                    <ListGroupItem>
                      <small className="text-muted">
                        {isUserLimitExhausted
                          ? "User limit exhausted, Buy Multiple users addon if available or Please upgrade your plan"
                          : "To add more users, enable the Multiple Users add-on if available."}
                      </small>
                    </ListGroupItem>
                  </ListGroup>
                )}

                <Card.Title className="text-success">Available Feature Add-ons</Card.Title>
                <ListGroup variant="flush">
                  {availableFeatureAddons.map((fa) => (
                    <ListGroupItem
                      key={fa.id}
                      className="d-flex justify-content-between align-items-center"
                      onClick={() => handleToggleFeature(fa)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span>
                        <div className="fw-semibold">{fa.label}</div>
                        <div className="text-muted small">₹ {formatAmount(fa.price)} • Tax {fa.tax_rate}%</div>
                      </span>
                      <Form.Check
                        type="checkbox"
                        checked={order_items.slice(2).some((it) => it.addon_id === fa.id)}
                        readOnly
                      />
                    </ListGroupItem>
                  ))}
                  {availableFeatureAddons.length === 0 && (
                    <ListGroupItem className="text-muted">
                      All available features are already included in your current plan.
                    </ListGroupItem>
                  )}
                </ListGroup>
              </Card.Body>
            </Card>

            <Card className="mb-5 mt-5 border">
              <Card.Header className="h4 d-flex justify-content-between align-items-center">
                <span>Billing Address</span>
                <Button variant="link" className="p-0" onClick={() => setIsEditAddress((v) => !v)} title={isEditAddress ? 'Close edit' : 'Edit address'}>
                  <UilEdit size={18} />
                </Button>
              </Card.Header>
              <Card.Body className="" style={{ borderTop: '1px solid lightgray' }}>
                {!isEditAddress && (
                  <div className="text-muted">
                    {(() => {
                      const a = order_billing_addresses?.[0] || {};
                      const parts = [a.name, a.phone_number, a.address1, a.address2, a.city, a.state, a.pincode, a.gstin]
                        .filter((x) => x && String(x).trim() !== "");
                      return parts.length ? parts.join(', ') : 'No billing address set yet.';
                    })()}
                  </div>
                )}
                {isEditAddress && (
                  <>
                    <Row>
                      <Col lg={6} className="mt-3"><Name /></Col>
                      <Col lg={6} className="mt-3"><PhoneNumber /></Col>
                    </Row>
                    <Row>
                      <Col lg={6} className="mt-3"><Address1 /></Col>
                      <Col lg={6} className="mt-3"><Address2 /></Col>
                    </Row>
                    <Row>
                      <Col lg={6} className="mt-3"><Gstin /></Col>
                      <Col lg={6} className="mt-3"><Pincode /></Col>
                    </Row>
                    <Row>
                      <Col lg={6} className="mt-3"><City /></Col>
                      <Col lg={6} className="mt-3"><State /></Col>
                    </Row>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card>
              <Card.Header className="text-white" style={{ background: "#00796B" }}>Summary</Card.Header>
              <Card.Body className="p-2 border">
                <ListGroup variant="flush">
                  <ListGroupItem>
                    Users Total
                    <span className="float-end">₹&nbsp;{formatAmount((order_items[1]?.quantity || 0) * (addons[0]?.price || 0))}</span>
                  </ListGroupItem>
                  <ListGroupItem>
                    Feature Add-ons Total
                    <span className="float-end">₹&nbsp;{formatAmount(order_items.slice(2).reduce((s, it) => s + it.price * it.quantity, 0))}</span>
                  </ListGroupItem>
                  <ListGroupItem>
                    Taxable Total
                    <span className="float-end">₹&nbsp;{formatAmount(taxable_value)}</span>
                  </ListGroupItem>
                  <ListGroupItem>
                    Tax
                    <span className="float-end">₹&nbsp;{formatAmount(tax_total)}</span>
                  </ListGroupItem>
                  <ListGroupItem className="h4">
                    Grand total
                    <span className="float-end">₹&nbsp;{formatAmount(grand_total)}</span>
                  </ListGroupItem>
                </ListGroup>
              </Card.Body>
              <Card.Footer className="border h3 p-3">
                <Button
                  variant="primary"
                  className="float-end"
                  onClick={handleCreateOrder}
                  disabled={order_items.slice(2).length === 0 && (order_items[1]?.quantity || 0) === 0}
                  title={(order_items.slice(2).length === 0 && (order_items[1]?.quantity || 0) === 0) ? 'Select at least one add-on' : ''}
                >
                  Proceed to pay
                </Button>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default ManageAddons;
